(() => {
  if (window.__p3dTextMarqueeLoaded) return;
  window.__p3dTextMarqueeLoaded = true;

  const SELECTOR = '[data-p3d-text-marquee]';
  const instances = new Map();

  class P3DTextMarquee {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-p3d-text-marquee-viewport]');
      this.track = section.querySelector('[data-p3d-text-marquee-track]');
      this.sourceGroup = section.querySelector('[data-p3d-text-marquee-group]');
      this.intersectionObserver = null;
      this.resizeObserver = null;
      this.resizeRaf = null;

      this.onResize = this.onResize.bind(this);

      this.applySettings();
      this.buildTrack();
      this.bindEvents();
      this.observeVisibility();
    }

    applySettings() {
      const d = this.section.dataset;

      this.section.style.setProperty('--p3dtm-bg', d.bgColor || '#ffffff');
      this.section.style.setProperty('--p3dtm-padding-top-desktop', `${parseInt(d.paddingTopDesktop || '36', 10)}px`);
      this.section.style.setProperty('--p3dtm-padding-top-mobile', `${parseInt(d.paddingTopMobile || '24', 10)}px`);
      this.section.style.setProperty('--p3dtm-padding-bottom-desktop', `${parseInt(d.paddingBottomDesktop || '36', 10)}px`);
      this.section.style.setProperty('--p3dtm-padding-bottom-mobile', `${parseInt(d.paddingBottomMobile || '24', 10)}px`);
      this.section.style.setProperty('--p3dtm-text-size-desktop', `${parseInt(d.textSizeDesktop || '56', 10)}px`);
      this.section.style.setProperty('--p3dtm-text-size-mobile', `${parseInt(d.textSizeMobile || '28', 10)}px`);
      this.section.style.setProperty('--p3dtm-text-color-desktop', d.textColorDesktop || '#111111');
      this.section.style.setProperty('--p3dtm-text-color-mobile', d.textColorMobile || '#111111');
      this.section.style.setProperty('--p3dtm-gap-desktop', `${parseInt(d.gapDesktop || '56', 10)}px`);
      this.section.style.setProperty('--p3dtm-gap-mobile', `${parseInt(d.gapMobile || '28', 10)}px`);
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    currentSpeed() {
      const d = this.section.dataset;
      return this.isMobile()
        ? parseInt(d.speedMobile || '65', 10)
        : parseInt(d.speedDesktop || '90', 10);
    }

    buildTrack() {
      if (!this.sourceGroup) return;

      const slides = Array.from(this.sourceGroup.children);
      if (!slides.length) return;

      this.track.innerHTML = '';

      const measureGroup = this.sourceGroup.cloneNode(true);
      measureGroup.classList.add('is-measure-only');
      this.track.appendChild(measureGroup);

      const sourceWidth = Math.ceil(measureGroup.getBoundingClientRect().width);
      const viewportWidth = Math.ceil(this.viewport.getBoundingClientRect().width);

      let repeats = 2;
      if (sourceWidth > 0 && viewportWidth > 0) {
        repeats = Math.max(2, Math.ceil((viewportWidth * 2) / sourceWidth) + 1);
      }

      this.track.innerHTML = '';

      for (let i = 0; i < repeats; i += 1) {
        const group = document.createElement('div');
        group.className = 'p3d-text-marquee__group';
        group.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');

        slides.forEach((slide) => {
          group.appendChild(slide.cloneNode(true));
        });

        this.track.appendChild(group);
      }

      const finalSourceWidth = Math.ceil(this.track.firstElementChild.getBoundingClientRect().width) || sourceWidth || viewportWidth || 1000;
      const speed = Math.max(20, this.currentSpeed());
      const duration = finalSourceWidth / speed;

      this.section.style.setProperty('--p3dtm-group-width', `${finalSourceWidth}px`);
      this.section.style.setProperty('--p3dtm-duration', `${duration.toFixed(2)}s`);
    }

    bindEvents() {
      window.addEventListener('resize', this.onResize);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.viewport);
      }
    }

    onResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);

      this.resizeRaf = requestAnimationFrame(() => {
        this.applySettings();
        this.buildTrack();
      });
    }

    observeVisibility() {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.section.classList.add('is-active');
            } else {
              this.section.classList.remove('is-active');
            }
          });
        },
        {
          root: null,
          rootMargin: '120px 0px 120px 0px',
          threshold: 0
        }
      );

      this.intersectionObserver.observe(this.section);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }
  }

  function init(scope = document) {
    const sections = scope.matches?.(SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SELECTOR) || []);

    sections.forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new P3DTextMarquee(section));
    });
  }

  function destroy(scope = document) {
    const sections = scope.matches?.(SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SELECTOR) || []);

    sections.forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  ready(() => init(document));

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();
